import React, { useEffect, useRef } from 'react';

import { fg } from '@confluence/feature-gating';

import { useMultiMacroQueryStoreHook } from '@confluence/fabric-extension-queries/entry-points/multiMacroQuery.ts';

import {
	StaticMacroStoreProvider,
	useStaticMacroStoreContext,
} from './StaticMacroStoreContext.tsx';

/**
 * This loads the multi-macro query store into the static macro store context.
 *
 * Sits in `MultiMacroQueryContainer` and `StaticMacroStoreProvider`, reads `staticMacroData` from the query store
 * and registers entries into the static macro context. Each `ForgeMacro` is only notified when its own data arrives.
 * When all staticMacros pages have been fetched, calls `markFullyLoaded` so pending macros know to fall back to the iframe renderer.
 */
export function StaticMacroDataBridge() {
	const ctx = useStaticMacroStoreContext();
	const [{ staticMacroData, staticMacrosComplete }] = useMultiMacroQueryStoreHook();

	// List of all currently registered localIds so we don't do duplicate work if we received a localId multiple times.
	const registeredLocalIds = useRef<Set<string>>(new Set());
	const prevStaticMacroPagesRef = useRef<Record<string, any> | null>(null);
	const fullyLoadedRef = useRef(false);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!ctx) return;

		// Start a 5s safety-net timeout in case the network never responds.
		// Cancelled if staticMacrosComplete becomes true via the normal path.
		if (!timeoutRef.current && !fullyLoadedRef.current) {
			timeoutRef.current = setTimeout(() => {
				if (!fullyLoadedRef.current) {
					fullyLoadedRef.current = true;
					ctx.markFullyLoaded();
				}
			}, 5_000);
		}

		if (staticMacroData !== prevStaticMacroPagesRef.current) {
			prevStaticMacroPagesRef.current = staticMacroData;

			for (const node of Object.values(staticMacroData ?? {})) {
				const { localId, value, error } = node;
				if (!localId || registeredLocalIds.current.has(localId)) continue;
				registeredLocalIds.current.add(localId);

				if (value) {
					ctx.register(localId, value);
				} else if (error) {
					ctx.registerError(localId, error.code);
				}
			}
		}

		if (staticMacrosComplete && !fullyLoadedRef.current) {
			// Network completed normally — cancel the timeout, it's not needed.
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
				timeoutRef.current = null;
			}
			fullyLoadedRef.current = true;
			ctx.markFullyLoaded();
		}
	}, [ctx, staticMacroData, staticMacrosComplete]);

	return null;
}

export function StaticMacrosBridge({ children = null }: { children?: React.ReactNode }) {
	if (!fg('conf_frontend_forge_static_macro_mvp')) {
		return children;
	}
	return (
		<StaticMacroStoreProvider>
			<StaticMacroDataBridge />
			{children}
		</StaticMacroStoreProvider>
	);
}
